import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, qs } from "../lib/api";
import { Icon } from "./Icon";
import { Modal } from "./ui";
import { useToast } from "./Toast";

/**
 * هويّةُ رقمٍ في مكالمة — من هو، ومن يصير إن لم يكن أحدًا.
 *
 * الرقمُ في سجلّ المكالمات ثلاثةُ أحوال:
 *
 *   - **عميلٌ محتمل** أو **حسابٌ** — يُفتح ملفُّه.
 *   - **جهةُ اتصال** — تُقرأ باسمها واسمِ حسابها: «سامي — شركة النور».
 *     جهةٌ بلا حسابها معلّقةٌ لا يُعرف أينَ تُتابَع.
 *   - **مجهول** — وهو أثمنُ ما في اليوم إن كان واردًا: عميلٌ طرق البابَ
 *     بنفسه. فيُضاف من مكانه محتملًا، أو جهةَ اتصالٍ في حسابٍ قائم إن كان
 *     موظفًا في عميلٍ نعرفه — فسجلٌّ موازٍ لعميلٍ قائم يشتّت تاريخَه.
 */

/** ما تحتاجه هذه المكوّنات من صفّ المكالمة. */
export interface CallIdentityRow {
  id: string;
  phone: string;
  leadId?: string | null;
  accountId?: string | null;
  contactId?: string | null;
  lead?: { id: string; name: string } | null;
  account?: { id: string; name: string } | null;
  contact?: {
    id: string;
    name: string;
    accountId?: string | null;
    account?: { id: string; name: string } | null;
  } | null;
}

/** الاسمُ المعروض وتبعيّتُه ورابطُ ملفّه — أو لا شيء إن كان مجهولًا. */
export function identityOf(c: CallIdentityRow): {
  name: string | null;
  belongsTo: string | null;
  link: string | null;
} {
  if (c.contact) {
    return {
      name: c.contact.name,
      belongsTo: c.contact.account?.name ?? null,
      link: c.contact.account?.id
        ? `/crm/accounts/${c.contact.account.id}`
        : c.contact.accountId
          ? `/crm/accounts/${c.contact.accountId}`
          : null,
    };
  }
  if (c.account)
    return {
      name: c.account.name,
      belongsTo: null,
      link: `/crm/accounts/${c.account.id}`,
    };
  if (c.lead)
    return {
      name: c.lead.name,
      belongsTo: "عميل محتمل",
      link: `/crm/leads/${c.lead.id}`,
    };
  return { name: null, belongsTo: null, link: null };
}

/**
 * زرُّ «أضِف» للرقم المجهول — يفتح نافذةَ الاختيار.
 *
 * الاختيارُ لا يُفترض: من يتّصل قد يكون محتملًا جديدًا وقد يكون موظفًا في
 * عميلٍ قائم، والفرقُ بينهما هو أين يُتابَع بعدُ.
 */
export function AddCallerButton({
  call,
  onDone,
  size = "sm",
}: {
  call: CallIdentityRow;
  onDone?: () => void;
  size?: "sm" | "xs";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`btn btn-${size === "xs" ? "sm" : "sm"} btn-primary nowrap`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="أضِف هذا الرقم عميلًا محتملًا أو جهةَ اتصالٍ في حسابٍ قائم"
      >
        <Icon name="plus" size={12} />
        أضِف
      </button>
      {open && (
        <AddCallerModal
          call={call}
          onClose={() => setOpen(false)}
          onDone={onDone}
        />
      )}
    </>
  );
}

function AddCallerModal({
  call,
  onClose,
  onDone,
}: {
  call: CallIdentityRow;
  onClose: () => void;
  onDone?: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<"lead" | "contact">("lead");
  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState("");

  /* حساباتُ الشركة — تُطلب حين تُفتح النافذة لا مع كل صفّ. */
  const accounts = useQuery({
    queryKey: ["accounts", "options"],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: "name", order: "asc" })}`,
      ),
    staleTime: 5 * 60_000,
  });

  const add = useMutation({
    mutationFn: () =>
      api<{
        leadId?: string;
        contactId?: string;
        accountId?: string;
        created: boolean;
      }>(`/callcenter/calls/${call.id}/lead`, {
        method: "POST",
        body: {
          kind,
          name: name.trim() || undefined,
          ...(kind === "contact" ? { accountId } : {}),
        },
      }),
    onSuccess: (r) => {
      toast(
        kind === "contact"
          ? r.created
            ? "أُضيفت جهةُ اتصالٍ وربُطت بها المكالمة"
            : "الرقم لجهةِ اتصالٍ قائمة — ربُطت بها"
          : r.created
            ? "أُضيف عميلًا محتملًا وربُطت به المكالمة"
            : "الرقم لمحتملٍ قائم — ربُطت به",
      );
      void queryClient.invalidateQueries({ queryKey: ["cc"] });
      void queryClient.invalidateQueries({ queryKey: ["calls"] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      onDone?.();
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّرت الإضافة."),
  });

  const ready = kind === "lead" || Boolean(accountId);

  return (
    <Modal
      title="أضِف هذا الرقم"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={!ready || add.isPending}
            onClick={() => add.mutate()}
          >
            <Icon name="check" size={14} />
            {add.isPending ? "جارٍ الحفظ" : "أضِف"}
          </button>
        </>
      }
    >
      <p className="tiny mute mb-3">
        الرقم{" "}
        <span className="num" dir="ltr">
          {call.phone}
        </span>{" "}
        غير مسجَّل عندنا. أضِفه محتملًا جديدًا، أو جهةَ اتصالٍ في حسابٍ قائم إن
        كان موظفًا عند عميلٍ نعرفه — وتُربط به المكالمة.
      </p>

      <div className="bp-choices mb-3">
        <button
          type="button"
          className={`bp-choice${kind === "lead" ? " is-on" : ""}`}
          aria-pressed={kind === "lead"}
          onClick={() => setKind("lead")}
        >
          <span className="bp-choice-icon">
            <Icon name="user" size={15} />
          </span>
          <span className="bp-choice-text">
            <strong>عميل محتمل</strong>
            <span className="tiny mute">
              فرصةٌ جديدة تُتابَع في قمع المبيعات.
            </span>
          </span>
          <span className="bp-choice-check">
            <Icon name="check" size={12} />
          </span>
        </button>
        <button
          type="button"
          className={`bp-choice${kind === "contact" ? " is-on" : ""}`}
          aria-pressed={kind === "contact"}
          onClick={() => setKind("contact")}
        >
          <span className="bp-choice-icon">
            <Icon name="building" size={15} />
          </span>
          <span className="bp-choice-text">
            <strong>جهة اتصال</strong>
            <span className="tiny mute">
              موظفٌ في عميلٍ قائم — مكالمتُه في ملفّ حسابه.
            </span>
          </span>
          <span className="bp-choice-check">
            <Icon name="check" size={12} />
          </span>
        </button>
      </div>

      <div className="form-grid">
        <label className="field">
          <span className="label">الاسم</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`متصل ${call.phone}`}
          />
          <span className="tiny mute">
            فارغًا يُسمّى برقمه حتى يُعرف اسمُه.
          </span>
        </label>

        {kind === "contact" && (
          <label className="field">
            <span className="label req">الحساب التابع له</span>
            <select
              className="select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">اختر حسابًا</option>
              {(accounts.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* الفتحُ بعد الحفظ اختياريّ: من أضاف وهو في السجل قد يريد إتمام عمله. */}
      {add.isSuccess && (
        <button
          className="btn btn-sm mt-3"
          onClick={() => {
            const r = add.data;
            if (r?.contactId && r.accountId)
              navigate(`/crm/accounts/${r.accountId}`);
            else if (r?.leadId) navigate(`/crm/leads/${r.leadId}`);
          }}
        >
          افتح الملفّ
        </button>
      )}
    </Modal>
  );
}

/** الاتجاه — واردٌ يدخل وصادرٌ يخرج، أيقونةً تُقرأ بلا نصّ. */
export function CallDirection({ direction }: { direction: string }) {
  const inbound = direction === "INBOUND";
  return (
    <span
      className={`cc-dir${inbound ? " is-in" : ""}`}
      title={inbound ? "واردة" : "صادرة"}
    >
      <Icon name={inbound ? "inbox" : "send"} size={13} />
    </span>
  );
}
